import React from "react";
import { useTranslation } from "react-i18next";
import { FaShieldAlt, FaUserPlus } from "react-icons/fa";

import useScrollToTop from "../../../hooks/useScollToTop";
import { useNotification } from "../../../components/Notification/NotificationManager";

import { usePageJudoka } from "./hooks/usePageJudoka";

import LoadingState from "./components/LoadingState";
import JudokaSelector from "./components/JudokaSelector";
import JudokaProfileHero from "./components/JudokaProfileHero";
import JudokaCategoryCard from "./components/JudokaCategoryCard";
import JudokaInfoGrid from "./components/JudokaInfoGrid";
import EditJudokaModal from "./components/EditJudokaModal";
import PhotoModal from "./components/PhotoModal";
import DeleteJudokaModal from "./components/DeleteJudokaModal";

import { getPhotoUrl } from "./utils/judokaProfileUtils";

function PageJudoka() {
  const { t } = useTranslation();
  useScrollToTop();

  const { addNotification } = useNotification();

  const {
    adherents,
    judoka,
    selectedJudokaId,
    localJudoka,

    isEditing,
    isPhotoModalOpen,

    selectedAvatar,
    photoUrl,
    photoUrlError,

    loading,
    saving,
    weightSaving,
    quickWeight,

    selectJudoka,
    openCreateModal,
    openEditModal,
    openPhotoModal,
    closeModals,

    handleChange,
    handleCountryChange,
    handleSubmit,

    handleAvatarPreview,
    handleAvatarSubmit,
    handlePhotoUrlChange,
    handlePhotoUrlSubmit,

    handleQuickWeightChange,
    handleQuickWeightAdjust,
    handleQuickWeightSave,

    isDeleteModalOpen,
    deleting,
    openDeleteModal,
    handleDeleteJudoka,
  } = usePageJudoka({ addNotification });

  if (loading) return <LoadingState />;

  return (
    <main className="relative min-h-screen overflow-hidden bg-slate-50 px-4 pb-16 pt-28 font-satoshi dark:bg-slate-950 sm:px-6 lg:px-8">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(249,115,22,0.14),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(37,99,235,0.16),transparent_32%)]" />
      <div className="absolute inset-x-0 top-0 h-72 bg-slate-950" />
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-orange-400/80 to-transparent" />

      <section className="relative mx-auto max-w-6xl">
        <div className="mb-7 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-3 py-2 text-xs font-bold uppercase tracking-[0.2em] text-orange-200 shadow-sm backdrop-blur-xl">
              <FaShieldAlt />
              Espace judoka
            </div>

            <h1 className="mt-4 font-titre text-4xl font-bold leading-tight text-white sm:text-5xl">
              {t("judokaProfile.title", {
                defaultValue: "Profil judoka",
              })}
            </h1>

            <p className="mt-3 max-w-2xl text-base font-medium leading-8 text-white/60 sm:text-lg">
              Gérez les profils judokas liés à votre compte : identité
              sportive, catégories, licence, club, photo et coordonnées de
              contact utiles à l’enseignant.
            </p>
          </div>
        </div>

        <JudokaSelector
          adherents={adherents}
          selectedId={selectedJudokaId}
          onSelect={selectJudoka}
          onAdd={openCreateModal}
        />

        {!judoka && !isEditing && (
          <div className="rounded-[2rem] border border-slate-200 bg-white p-6 text-center shadow-xl shadow-slate-200/60 dark:border-white/10 dark:bg-white/[0.05] dark:shadow-black/20">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-3xl bg-orange-100 text-orange-700 dark:bg-orange-500/10 dark:text-orange-200">
              <FaUserPlus className="text-2xl" />
            </div>

            <h2 className="mt-5 font-titre text-2xl font-bold text-slate-950 dark:text-white">
              Aucun profil judoka trouvé
            </h2>

            <p className="mx-auto mt-3 max-w-xl text-sm font-medium leading-7 text-slate-600 dark:text-white/55">
              Créez un premier profil pour afficher les catégories, le club, la
              licence, les informations sportives et les coordonnées de
              contact.
            </p>

            <button
              type="button"
              onClick={openCreateModal}
              className="mt-6 inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-blue-600 to-orange-500 px-5 py-3 text-sm font-bold text-white shadow-lg shadow-orange-500/20 transition hover:-translate-y-0.5"
            >
              <FaUserPlus />
              Créer un profil judoka
            </button>
          </div>
        )}

        {judoka && (
          <div className="grid gap-6 lg:grid-cols-[0.9fr_1.1fr] lg:items-start">
            <article className="overflow-hidden rounded-[2rem] border border-white/10 bg-white shadow-2xl shadow-slate-200/70 dark:bg-white/[0.06] dark:shadow-black/25">
              <JudokaProfileHero
                judoka={judoka}
                onChangePhoto={openPhotoModal}
                t={t}
              />

              <JudokaCategoryCard judoka={judoka} />
            </article>

            <JudokaInfoGrid
              judoka={judoka}
              quickWeight={quickWeight}
              weightSaving={weightSaving}
              onEdit={openEditModal}
              onDelete={openDeleteModal}
              onWeightChange={handleQuickWeightChange}
              onWeightAdjust={handleQuickWeightAdjust}
              onWeightSave={handleQuickWeightSave}
              t={t}
            />
          </div>
        )}
      </section>

      {isEditing && (
        <EditJudokaModal
          judoka={localJudoka?.id ? localJudoka : null}
          form={localJudoka}
          onChange={handleChange}
          onCountryChange={handleCountryChange}
          onSubmit={handleSubmit}
          onClose={closeModals}
          saving={saving}
          t={t}
        />
      )}

      {isPhotoModalOpen && (
        <PhotoModal
          currentPhoto={getPhotoUrl(judoka)}
          selectedAvatar={selectedAvatar}
          photoUrl={photoUrl}
          photoUrlError={photoUrlError}
          onPhotoUrlChange={handlePhotoUrlChange}
          onAvatarPreview={handleAvatarPreview}
          onAvatarSubmit={handleAvatarSubmit}
          onPhotoUrlSubmit={handlePhotoUrlSubmit}
          onClose={closeModals}
          saving={saving}
          t={t}
        />
      )}

      {isDeleteModalOpen && (
        <DeleteJudokaModal
          judoka={judoka}
          deleting={deleting}
          onClose={closeModals}
          onConfirm={handleDeleteJudoka}
        />
      )}
    </main>
  );
}

export default PageJudoka;